import axios from 'axios'

// Client HTTP de l'espace client.
//
// DEUX RESPONSABILITÉS, et rien d'autre : porter le jeton d'accès, et le
// renouveler quand il périme. Tout le reste appartient aux vues.
//
// Le jeton d'accès vit 15 minutes, celui de rafraîchissement 7 jours. Une
// session de travail dépasse donc largement la vie d'un jeton d'accès : sans
// renouvellement automatique, le client serait déconnecté en pleine lecture.

const BASE = (import.meta.env.VITE_API_URL || 'https://api.reunibox.com').replace(/\/+$/, '')

// Clés propres à cet espace. Les autres applications du domaine utilisent
// `accessToken` et `domAdminToken` : des noms distincts permettent aux
// sessions de coexister si elles venaient un jour à partager une origine.
const CLE_ACCES = 'espaceClientAcces'
const CLE_RAFRAICHISSEMENT = 'espaceClientRafraichissement'
// Marque un aperçu administrateur. Elle sert à deux choses : ne PAS tenter de
// rafraîchir (il n'y a pas de jeton pour ça, par construction), et expliquer
// la fin de l'aperçu au lieu de renvoyer sur un formulaire de connexion qui
// n'a aucun sens pour un administrateur.
const CLE_APERCU = 'espaceClientApercu'

export const jetons = {
  acces: () => localStorage.getItem(CLE_ACCES) || '',
  rafraichissement: () => localStorage.getItem(CLE_RAFRAICHISSEMENT) || '',
  poser(acces, rafraichissement) {
    if (acces) localStorage.setItem(CLE_ACCES, acces)
    if (rafraichissement) localStorage.setItem(CLE_RAFRAICHISSEMENT, rafraichissement)
  },
  effacer() {
    localStorage.removeItem(CLE_ACCES)
    localStorage.removeItem(CLE_RAFRAICHISSEMENT)
    localStorage.removeItem(CLE_APERCU)
  },
  // Aperçu administrateur : un jeton d'accès court, et SURTOUT aucun jeton
  // de rafraîchissement. Pour rouvrir un aperçu il faut repasser par
  // l'administration, donc par le journal.
  poserApercu(acces) {
    if (!acces) return
    localStorage.removeItem(CLE_RAFRAICHISSEMENT)
    localStorage.setItem(CLE_ACCES, acces)
    localStorage.setItem(CLE_APERCU, '1')
  },
  estApercu: () => localStorage.getItem(CLE_APERCU) === '1',
}

const http = axios.create({ baseURL: BASE })

http.interceptors.request.use(config => {
  const jeton = jetons.acces()
  if (jeton) config.headers['x-access-token'] = jeton
  return config
})

// Un seul rafraîchissement à la fois. Le tableau de bord lance quatre
// requêtes en parallèle : sans cette promesse partagée, quatre
// renouvellements partiraient de front et trois jetons seraient aussitôt
// périmés par le quatrième.
let rafraichissementEnCours = null

function rafraichir() {
  if (rafraichissementEnCours) return rafraichissementEnCours
  const jeton = jetons.rafraichissement()
  if (!jeton) return Promise.reject(new Error('aucun jeton de rafraîchissement'))

  rafraichissementEnCours = axios
    .post(`${BASE}/client/rafraichir`, { rafraichissement: jeton })
    .then(reponse => {
      const acces = (reponse.data || {}).acces
      if (!acces) throw new Error('rafraîchissement sans jeton')
      jetons.poser(acces, null)
      return acces
    })
    .finally(() => { rafraichissementEnCours = null })

  return rafraichissementEnCours
}

// Sur 401, on tente UNE fois le renouvellement puis on rejoue la requête.
// `_rejoue` empêche la boucle : si la requête rejouée échoue encore, la
// session est bel et bien finie.
http.interceptors.response.use(
  reponse => reponse,
  async erreur => {
    const requete = erreur.config || {}
    const statut = erreur.response && erreur.response.status
    const estRafraichissement = String(requete.url || '').includes('/client/rafraichir')

    // En aperçu, il n'y a rien à rafraîchir : le jeton est court à dessein.
    // Tenter le renouvellement effacerait la session et renverrait
    // l'administrateur sur le formulaire de connexion sans rien expliquer.
    if (statut === 401 && jetons.estApercu()) {
      return Promise.reject(erreur)
    }
    if (statut !== 401 || requete._rejoue || estRafraichissement) {
      return Promise.reject(erreur)
    }
    try {
      await rafraichir()
    } catch (e) {
      jetons.effacer()
      return Promise.reject(erreur)
    }
    requete._rejoue = true
    return http(requete)
  },
)

export default http

// --- appels, nommés par ce qu'ils rapportent ---------------------------

export const api = {
  demanderLien: email => http.post('/client/lien', { email }),
  ouvrirSession: token => http.post('/client/session', { token }),
  moi: () => http.get('/client/moi'),
  contrats: params => http.get('/client/contrats', { params }),
  factures: params => http.get('/client/factures', { params }),
  documents: params => http.get('/client/documents', { params }),
  demandes: params => http.get('/client/demandes', { params }),
}
